import type { LngLat } from '@maptiler/sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigationContext } from '@/features/navigation/provider';
import AerialMapModal from '@/features/navigation/topNav/aerialMapModal';
import {
  aerialMapboxBackgroundLayer,
  aerialMapboxBackgroundLayerSourceId,
  aerialMapboxLayer,
  aerialMapboxLayerSourceId,
} from '@/features/navigation/topNav/layers/aerialMapLayers';
import {
  addPreviewBoxToMap,
  createImageFromMapBounds,
  getNormalizedScalingFactor,
  Orientation,
} from '@/features/selectionsPanel/exportMenu/aerialMap/utils';
import useWindowDimensions from '@/hooks/useWindowDimensions';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import { useModalContext } from '@/providers/ModalProvider';

export const useInitializeAerialMap = () => {
  const { setModalContent, clearModalContent } = useModalContext();
  const { setAerialMapIsOpen, closeAerialMap } = useNavigationContext();
  const windowDimensions = useWindowDimensions();
  const { activeFileId } = useFileManagementContext();

  const { mapInstance: map } = useMapboxContext();

  const [selectedOrientation, setSelectedOrientation] = useState<Orientation>(
    Orientation.LANDSCAPE
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [captureBounds, setCaptureBounds] = useState<LngLat[] | null>(null);

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === 'Escape') closeAerialMap();
    },
    [closeAerialMap]
  );

  const captureImageFromBounds = useCallback(() => {
    if (!map || !captureBounds) return;
    setIsLoading(true);

    const bottomLeft = map.project(captureBounds[0]);
    const topRight = map.project(captureBounds[1]);

    map.once('render', async () => {
      const imageAsFile = await createImageFromMapBounds(
        bottomLeft,
        topRight,
        selectedOrientation
      );
      if (!imageAsFile) return;

      setModalContent({
        content: (
          <AerialMapModal
            activeFileId={activeFileId}
            image={imageAsFile}
            orientation={selectedOrientation}
          />
        ),
        onCloseEvent: clearModalContent,
      });

      setIsLoading(false);
    });
  }, [
    map,
    captureBounds,
    setModalContent,
    activeFileId,
    selectedOrientation,
    clearModalContent,
  ]);

  // Calculate the scaling factor based on the selected orientation
  // We can fit more content in landscape mode
  const normalizedScalingFactor = useMemo(() => {
    return getNormalizedScalingFactor(windowDimensions, selectedOrientation);
  }, [selectedOrientation, windowDimensions]);

  const renderPreviewOnMap = useCallback(() => {
    if (!map) return;
    const previewBounds = addPreviewBoxToMap(
      map,
      normalizedScalingFactor,
      selectedOrientation
    );
    if (!previewBounds) return;
    const [newBottomLeft, newTopRight] = previewBounds;
    setCaptureBounds([newBottomLeft, newTopRight]);
  }, [map, normalizedScalingFactor, selectedOrientation]);

  const initialize = useCallback(async () => {
    if (!map) return;

    setAerialMapIsOpen(true);
    document.addEventListener('keydown', handleKeyPress);

    if (!map.getLayer(aerialMapboxLayer.id)) {
      map.addLayer(aerialMapboxLayer);

      if (!map.getLayer(aerialMapboxBackgroundLayer.id)) {
        map.addLayer(aerialMapboxBackgroundLayer);
      }
    }

    renderPreviewOnMap();

    // Move the box when the map moves
    map.on('move', renderPreviewOnMap);
  }, [map, setAerialMapIsOpen, handleKeyPress, renderPreviewOnMap]);

  const cleanUp = useCallback(() => {
    if (!map) return;

    map.off('move', renderPreviewOnMap);

    if (map.getLayer(aerialMapboxLayer.id)) {
      map.removeLayer(aerialMapboxLayer.id);
    }
    if (map.getSource(aerialMapboxLayerSourceId)) {
      map.removeSource(aerialMapboxLayerSourceId);
    }

    if (map.getLayer(aerialMapboxBackgroundLayer.id)) {
      map.removeLayer(aerialMapboxBackgroundLayer.id);
    }

    if (map.getSource(aerialMapboxBackgroundLayerSourceId)) {
      map.removeSource(aerialMapboxBackgroundLayerSourceId);
    }

    setCaptureBounds(null);
    setAerialMapIsOpen(false);

    document.removeEventListener('keydown', handleKeyPress);
  }, [map, renderPreviewOnMap, setAerialMapIsOpen, handleKeyPress]);

  useEffect(() => {
    if (!map) return () => {};

    initialize();

    return cleanUp;
  }, [cleanUp, initialize, isLoading, map]);

  return {
    captureImageFromBounds,
    isLoading,
    selectedOrientation,
    setSelectedOrientation,
  };
};
